import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import styled from '@emotion/styled';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { startAtToPage, totalToNumPages } from '../components/util/pagination-helper';
import { DTOContext, DTOCustomField, DTOPaginatedContextsAndUserCapabilities, ErrorOrPayload, GET_CUSTOMFIELD_ALL_CONTEXTS_PAGINATED, USER_CAPABILITY_GRANT_PERMISSIONS } from '../generated-common';
import { LoadingState } from '../model/model';
import { BetterDynamicTable, BetterTableHead, BetterTablePaginationState, BetterTableRow } from '../shared-components/common/BetterDynamicTable';
import { GlobalContext } from '../shared-components/common/GlobalContext';
import { CepPageHeader, TBreadcrumbsItem } from '../shared-components/stateless/CepPageHeader';
import { CutIfTooBig } from '../shared-components/stateless/CutIfTooBig';
import { PageWrapper } from '../shared-components/stateless/layout/PageWrapper';
import { NoWrap } from '../shared-components/stateless/NoWrap';
import { PullRight } from '../shared-components/stateless/PullRight';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { PermissionEditorEditPermissions } from 'components/permission-editor/PermissionEditorEditPermissions';
import { sanitizeUserInput_fieldId, sanitizeUserInput_filter, toIntNullSafe } from 'generated-common/util';
import { CepHeaderFieldDetails } from 'shared-components/stateless/CepHeaderFieldDetails';
import { PageLoadError } from 'components/ui/PageLoadError';
import { token } from '@atlaskit/tokens';

const MAX_RESULTS = 10;

const InnerTd = styled.div<{ isFirst: boolean; isLast: boolean; isHiddenOnNonFirst: boolean }>`
  padding-right: 8px;
  padding-left: 8px;
  padding-top: ${(props: any) => (props.isFirst ? '10px' : '10px')};
  padding-bottom: ${(props: any) => (props.isLast ? '6px' : '6px')};
  display: ${(props: any) => (!props.isFirst && props.isHiddenOnNonFirst ? 'none' : 'block')};
`;

const InnerTh = styled.div`
  padding-left: 8px;
`;

export const FieldEditorListContextsPage = () => {
  const { invoke, t, tWithIcon, encodeUriSafe } = useContext(GlobalContext);
  const navigate = useNavigate();

  // const fieldId = query.get('fieldId');
  let { fieldId } = useParams<{ fieldId: any }>();
  const fieldIdSanitized = sanitizeUserInput_fieldId(fieldId);
  const [userCapabilities, setUserCapabilities] = useState<string[]>([]);

  const [contexts, setContexts] = useState<DTOContext[]>([]);
  const [field, setField] = useState<DTOCustomField | null>(null);
  const [currentFilter, setCurrentFilter] = useState<string>('');
  const [currentPaginationState, setCurrentPaginationState] = useState<BetterTablePaginationState>({
    currentPage: 1,
    totalPages: 1,
    processingTimeInMs: 0,
  });
  const [loadingState, setLoadingState] = useState(LoadingState.NOT_LOADED);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [errorId, setErrorId] = useState<null | string>(null);

  const breadcrumbItems: TBreadcrumbsItem[] = [
    {
      href: '/fe/fields',
      i18n: 'user.list.breadcrumbTitle',
      params: [],
    },
    {
      href: '/fe/fields/:fieldId/contexts',
      i18n: 'user.listContext.breadcrumbTitle',
      params: [{ name: 'fieldId', value: fieldIdSanitized, type: 'path' }],
    },
  ];

  useEffect(() => {
    setLoadingState(LoadingState.LOADING);

    const startAt = (currentPaginationState.currentPage - 1) * MAX_RESULTS;
    invoke(GET_CUSTOMFIELD_ALL_CONTEXTS_PAGINATED, { fieldId: fieldIdSanitized, startAt: startAt, maxResults: MAX_RESULTS, currentFilter: sanitizeUserInput_filter(currentFilter) }).then(
      (result: any) => {
        const typedResult: ErrorOrPayload<DTOPaginatedContextsAndUserCapabilities> = result;
        if (typedResult.hasError) {
          setContexts([]);
          setHasError(true);
          setError(typedResult.errorMessage ? typedResult.errorMessage : 'Unexpected error');
          setErrorId(typedResult.errorId);
          setLoadingState(LoadingState.FAILED);
        } else {
          setHasError(false);
          setError(null);
          setErrorId(null);
          setContexts(typedResult.payload.contexts.values);
          setField(typedResult.payload.field);
          setUserCapabilities(typedResult.payload.userCapabilities);
          setCurrentPaginationState({
            currentPage: startAtToPage(typedResult.payload.contexts.startAt, MAX_RESULTS, typedResult.payload.contexts.total),
            totalPages: totalToNumPages(MAX_RESULTS, typedResult.payload.contexts.total),
            processingTimeInMs: typedResult.payload.contexts.processingTimeInMs,
          });
          setLoadingState(LoadingState.LOADED);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter, currentPaginationState.currentPage]);

  const head: BetterTableHead = {
    cells: [
      {
        key: 'fieldId',
        content: <InnerTh>{t('admin.listContext.tableHead.id')}</InnerTh>,
        isSortable: false,
        widthPercent: '6%',
      },
      {
        key: 'fieldName',
        content: <InnerTh>{t('admin.listContext.tableHead.name')}</InnerTh>,
        isSortable: false,
        widthPercent: '28%',
      },
      {
        key: 'actions',
        content: '',
        isSortable: false,
        widthPercent: '8%',
      },
    ],
  };

  const rows: BetterTableRow[] = [];
  if (contexts) {
    for (let index = 0; index < contexts.length; index++) {
      const context: DTOContext = contexts[index];

      rows.push({
        key: `row-${context.contextId}-${index}-` + Math.random(),
        doHoverTr: true,
        cells: [
          {
            key: `id-${index}-${context.contextId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <NoWrap>{context.contextId}</NoWrap>
              </InnerTd>
            ),
          },
          {
            key: `name-${index}-${context.contextId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <span style={{ color: token('color.text.accent.blue.bolder', '#253858'), fontWeight: 500, wordBreak: 'break-all' }}>{context.contextName}</span>
                <br />
                <CutIfTooBig tooltipText={context.contextDescription} color={token('color.text.accent.gray', '#7A869A')} text={context.contextDescription} />
              </InnerTd>
            ),
          },
          {
            key: `actions-${index}-${context.contextId}` + Math.random(),
            content: (
              <InnerTd isFirst={true} isLast={true} isHiddenOnNonFirst={false}>
                <div style={{ display: 'block', marginTop: '-4px' }} data-e2e-edit-context={`${context.contextId}`}>
                  <PullRight>
                    {userCapabilities.includes(USER_CAPABILITY_GRANT_PERMISSIONS) && (
                      <PermissionEditorEditPermissions
                        isGlobal={false}
                        fieldId={fieldIdSanitized}
                        fieldName={field ? field.fieldName : '...'}
                        contextId={toIntNullSafe(context.contextId)}
                        contextName={context.contextName}
                        isDisabled={field ? field.fieldIsLocked === true : true}
                      />
                    )}
                    <Tooltip content={t('user.list.editOptionsButton')}>
                      <Button
                        href={encodeUriSafe(
                          '/fe/fields/:fieldId/contexts/:contextId',
                          [
                            { name: 'fieldId', value: fieldIdSanitized, type: 'path' },
                            { name: 'contextId', value: context.contextId, type: 'path' },
                          ],
                          true
                        )}
                        iconBefore={<EditFilledIcon size="medium" label="edit" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.preventDefault();
                          navigate(
                            encodeUriSafe(
                              '/fe/fields/:fieldId/contexts/:contextId',
                              [
                                { name: 'fieldId', value: fieldIdSanitized, type: 'path' },
                                { name: 'contextId', value: context.contextId, type: 'path' },
                              ],
                              false
                            )
                          );
                        }}
                        appearance="subtle"
                        isDisabled={field ? field.fieldIsLocked === true : true}
                      />
                    </Tooltip>
                  </PullRight>
                </div>
              </InnerTd>
            ),
          },
        ],
      });
    }
  }

  return (
    <PageWrapper>
      <CepPageHeader
        useNavigate={useNavigate}
        pageTitle={field ? field.fieldName : hasError ? t('global.pageLoadFailErrorHeadline') : undefined}
        isLoading={loadingState === LoadingState.LOADING}
        breadcrumbItems={breadcrumbItems}
        helpUri={'/user-guide/field-editor/#contexts'}
        helpContent={
          <>
            <p>{t('user.listContext.notice.paragraph1')}</p>
            <ul>
              <li>{t('user.listContext.notice.paragraph2')}</li>
              <li>{tWithIcon('user.listContext.notice.paragraph3')}</li>
            </ul>
            {userCapabilities.includes(USER_CAPABILITY_GRANT_PERMISSIONS) && (
              <>
                <p>{t('user.listContext.notice.admin.paragraph1')}</p>
                <ul>
                  <li>{tWithIcon('user.listContext.notice.admin.paragraph2')}</li>
                </ul>
              </>
            )}
          </>
        }
      ></CepPageHeader>
      {hasError ? (
        <PageLoadError errorMessage={error} invokeKey={GET_CUSTOMFIELD_ALL_CONTEXTS_PAGINATED} errorId={errorId ? errorId : 'ERRLDNG'} />
      ) : (
        <>
          <CepHeaderFieldDetails
            isLoading={loadingState === LoadingState.LOADING}
            fieldType={field ? field.fieldType : ''}
            fieldName={field ? field.fieldName : ''}
            fieldDescription={field ? field.fieldDescription : ''}
            contextName={null}
            contextDescription={null}
          ></CepHeaderFieldDetails>
          <BetterDynamicTable
            rows={rows}
            head={head}
            hasFilter={true}
            hasPages={true}
            onSortChanged={() => {}}
            initialSort={{
              key: 'id',
              sortOrder: 'ASC',
            }}
            initialPage={currentPaginationState.currentPage}
            processingTime={currentPaginationState.processingTimeInMs}
            numPages={currentPaginationState.totalPages}
            onPageChanged={(newPage: number) => {
              setCurrentPaginationState((prev) => Object.assign({}, prev, { currentPage: newPage }));
            }}
            onFilterChanged={(f: string) => {
              setCurrentPaginationState((prev) => Object.assign({}, prev, { currentPage: 1 }));
              setCurrentFilter(f);
            }}
            initialFilter={currentFilter}
            isLoading={loadingState === LoadingState.LOADING}
            noResultsTitle={currentFilter !== '' ? t('user.listContext.results.noResultsOnFilter') : t('user.listContext.results.noResultsWithoutFilter')}
            noResultsDescription={currentFilter !== '' ? t('user.listContext.results.noResultsOnFilterDescription') : t('user.listContext.results.noResultsWithoutFilterDescription')}
          />
        </>
      )}
    </PageWrapper>
  );
};
